import React from "react";
import type { SearchResult } from "../types";

interface ResultsListProps {
  results: SearchResult[];
  loading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasSearched: boolean;
  noResultsUrl?: string;
  noResultsMessage?: string;
}

const ResultsList: React.FC<ResultsListProps> = ({
  results,
  loading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  hasSearched,
  noResultsUrl = "https://mercedca.justfoia.com/publicportal/home/newrequest",
  noResultsMessage = "No public records were found matching your search criteria. You may submit a formal document request through the City of Merced portal.",
}) => {
  const handleDownload = (blobUrl: string) => {
    window.open(blobUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-16">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-merced-blue-700 mb-4"></div>
          <p className="text-merced-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (results.length === 0 && hasSearched) {
    return (
      <div className="flex flex-col items-center justify-center py-14 px-6 bg-white rounded-lg shadow text-center space-y-5 animate-scale-in">
        <div className="w-14 h-14 bg-merced-blue-50 rounded-full flex items-center justify-center">
          <svg className="w-7 h-7 text-merced-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-semibold text-merced-gray-800 mb-2">No Documents Found</h3>
          <p className="text-sm text-merced-gray-500 max-w-sm">{noResultsMessage}</p>
        </div>
        <a
          href={noResultsUrl}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-merced-blue-700 text-white text-sm font-medium rounded-md hover:bg-merced-blue-800 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          Submit Document Request
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    );
  }

  if (results.length === 0 && !hasSearched) {
    return null;
  }

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-merced-gray-800">
          Found {totalCount} document{totalCount !== 1 ? "s" : ""}
        </h2>
      </div>

      {/* Table for Desktop */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-merced-gray-200">
            <thead className="bg-merced-blue-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Document Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Contract / Resolution No.
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Agency/Party
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Subject/Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                  Calendar Year
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-merced-gray-200">
              {results.map((doc, index) => (
                <tr
                  key={doc.id}
                  className={`hover:bg-merced-gray-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-merced-gray-50"
                    }`}
                >
                  <td className="px-4 py-3 text-sm text-merced-gray-900 font-medium">
                    <div
                      className="max-w-xs truncate"
                      title={doc.original_file_name || doc.file_name}
                    >
                      {doc.original_file_name || doc.file_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-merced-gray-600">
                    {doc.reference_number || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-merced-gray-600">
                    <div
                      className="max-w-xs truncate"
                      title={doc.related_party || ""}
                    >
                      {doc.related_party || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-merced-gray-600">
                    <div
                      className="max-w-sm truncate"
                      title={doc.subject_location || ""}
                    >
                      {doc.subject_location || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-merced-gray-600 whitespace-nowrap">
                    {doc.document_year || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleDownload(doc.blob_url)}
                      className="inline-flex items-center justify-center w-8 h-8 text-merced-blue-700 hover:bg-merced-blue-50 rounded-full transition-colors"
                      aria-label="Download document"
                      title="Download document"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compact Cards for Mobile/Tablet */}
      <div className="lg:hidden space-y-3 stagger">
        {results.map((doc) => (
          <div
            key={doc.id}
            className="bg-white rounded-lg shadow p-4 hover-lift hover:shadow-md"
          >
            <div className="space-y-2">
              <div className="flex justify-between items-start gap-2">
                <h3 className="text-sm font-semibold text-merced-blue-700 break-words flex-1">
                  {doc.original_file_name || doc.file_name}
                </h3>
                <button
                  onClick={() => handleDownload(doc.blob_url)}
                  className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 text-merced-blue-700 hover:bg-merced-blue-50 rounded-full transition-colors"
                  aria-label="Download"
                  title="Download document"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>

              <div className="text-xs space-y-1 text-merced-gray-600">
                {doc.reference_number && (
                  <div>
                    <span className="font-medium">Contract / Resolution No.:</span>{" "}
                    {doc.reference_number}
                  </div>
                )}
                {doc.related_party && (
                  <div>
                    <span className="font-medium">Agency:</span>{" "}
                    {doc.related_party}
                  </div>
                )}
                {doc.subject_location && (
                  <div>
                    <span className="font-medium">Subject:</span>{" "}
                    {doc.subject_location}
                  </div>
                )}
                {doc.document_year && (
                  <div>
                    <span className="font-medium">Year:</span>{" "}
                    {doc.document_year}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 py-6">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${currentPage === 1
              ? "text-merced-gray-400 border border-merced-gray-200 cursor-not-allowed"
              : "text-merced-gray-700 border border-merced-gray-300 hover:bg-merced-gray-50"
              }`}
          >
            ‹ Prev
          </button>

          <div className="flex gap-1">
            {(() => {
              const pages: (number | "ellipsis")[] = [];

              // Always show page 1
              pages.push(1);

              if (totalPages <= 5) {
                // Show all pages if 5 or fewer
                for (let i = 2; i <= totalPages; i++) pages.push(i);
              } else {
                // Determine middle pages around current
                let start = Math.max(2, currentPage - 1);
                let end = Math.min(totalPages - 1, currentPage + 1);

                // Adjust if current is near the start
                if (currentPage <= 3) {
                  start = 2;
                  end = Math.min(4, totalPages - 1);
                }
                // Adjust if current is near the end
                if (currentPage >= totalPages - 2) {
                  start = Math.max(2, totalPages - 3);
                  end = totalPages - 1;
                }

                // Left ellipsis
                if (start > 2) pages.push("ellipsis");

                // Middle pages
                for (let i = start; i <= end; i++) pages.push(i);

                // Right ellipsis
                if (end < totalPages - 1) pages.push("ellipsis");

                // Always show last page
                pages.push(totalPages);
              }

              return pages.map((item, idx) =>
                item === "ellipsis" ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="px-3 py-2 text-sm text-merced-gray-500 select-none"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => onPageChange(item)}
                    className={`min-w-[40px] px-3 py-2 text-sm font-medium rounded-md transition-all ${currentPage === item
                      ? "text-white bg-merced-blue-700 border border-merced-blue-700"
                      : "text-merced-gray-700 border border-merced-gray-300 hover:bg-merced-gray-50"
                      }`}
                  >
                    {item}
                  </button>
                ),
              );
            })()}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${currentPage === totalPages
              ? "text-merced-gray-400 border border-merced-gray-200 cursor-not-allowed"
              : "text-merced-gray-700 border border-merced-gray-300 hover:bg-merced-gray-50"
              }`}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultsList;
